import React, { useEffect, useState } from 'react';
import { ActionIcon, Box, Button, Flex, Group, Menu, Modal, Paper, Text, ThemeIcon } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconBrandWhatsapp,
  IconClock,
  IconCopy,
  IconDots,
  IconX,
} from '@tabler/icons-react';
import Fetch from '@/utils/Fetch';
import Helper from '@/utils/helper';
import Time from '@/utils/time';
import { InviteStatus, showsInvite } from '@/components/employee/employee-invite-header';

interface InviteLink {
  link?: string;
  whatsAppUrl?: string;
}

interface Props {
  employeeId: number | string;
  status?: InviteStatus;
  issuedAt?: string | null;
  expiresAt?: string | null;
  heldLink?: InviteLink | null;
  onChanged: () => void;
  setConfirmOptions: (options: any) => void;
}

const revokeConfirmText = 'Revoke this invite? The link will stop working and their login access will be turned off.';

const formatDay = (iso?: string | null) => (iso ? Time.formatDate(iso) : '');

const formatMoment = (iso?: string | null) =>
  iso ? `${Time.formatDate(iso)} ${Time.getTimeFormatted(iso, 'hh:mm')}` : '';

const readRetryAfterSeconds = (result: any) => {
  if (typeof result?.RetryAfterSeconds === 'number') return result.RetryAfterSeconds;
  const parsed = Helper.parseString(result?.serverMessage);
  return typeof parsed?.RetryAfterSeconds === 'number' ? parsed.RetryAfterSeconds : null;
};

const isThrottled = (result: any) => result?.status === 429 || result?.HttpStatusCode === 429;

const formatWait = (seconds: number) => {
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
};

const waitingLine = (issuedAt?: string | null, expiresAt?: string | null) =>
  [issuedAt && `Invite sent ${formatMoment(issuedAt)}`, expiresAt && `Expires ${formatDay(expiresAt)}`]
    .filter(Boolean)
    .join(' · ');

const expiredLine = (expiresAt?: string | null) =>
  `${expiresAt ? `Expired ${formatDay(expiresAt)}. ` : ''}Send a new invite so they can sign in.`;

function EmployeeInvitePanel({ employeeId, status, issuedAt, expiresAt, heldLink, onChanged, setConfirmOptions }: Props) {
  const [issuedLink, setIssuedLink] = useState<InviteLink | null>(null);
  const [sharing, setSharing] = useState<InviteLink | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setIssuedLink(null);
  }, [status, employeeId]);

  if (!showsInvite(status)) return null;

  const currentLink = issuedLink ?? heldLink ?? null;

  const requestNewLink = async (): Promise<InviteLink | null> => {
    setBusy(true);
    let result: any;
    try {
      result = await Fetch.post({ url: `/Employee/ResendInvite/${employeeId}` });
    } catch (err: any) {
      result = err;
    }
    setBusy(false);

    if (isThrottled(result)) {
      showNotification({
        color: 'yellow.7',
        message: `Please wait ${formatWait(readRetryAfterSeconds(result) ?? 60)} before resending.`,
        autoClose: 5000,
      });
      return null;
    }

    if (result?.InviteLink || result?.InviteWhatsAppUrl) {
      const fresh = { link: result.InviteLink, whatsAppUrl: result.InviteWhatsAppUrl };
      setIssuedLink(fresh);
      onChanged();
      return fresh;
    }

    showNotification({
      color: 'yellow.7',
      message: result?.Message || result?.message || 'Could not resend the invite.',
      autoClose: 5000,
    });
    return null;
  };

  const resendAndShow = async () => {
    const fresh = await requestNewLink();
    if (fresh) setSharing(fresh);
  };

  const copy = (link?: string) => {
    if (!link) return;
    Helper.copyToClipboard(link);
    showNotification({ color: 'green.7', message: 'Copied', autoClose: 2000 });
  };

  const copyLink = async () => {
    if (currentLink?.link) return copy(currentLink.link);
    const fresh = await requestNewLink();
    copy(fresh?.link);
  };

  const shareOnWhatsApp = async () => {
    if (currentLink?.whatsAppUrl) {
      window.open(currentLink.whatsAppUrl, '_blank', 'noreferrer');
      return;
    }
    const popup = window.open('', '_blank');
    const fresh = await requestNewLink();
    if (fresh?.whatsAppUrl && popup) {
      popup.location.href = fresh.whatsAppUrl;
    } else {
      popup?.close();
    }
  };

  const revoke = async () => {
    setBusy(true);
    let result: any;
    try {
      result = await Fetch.post({ url: `/Employee/RevokeInvite/${employeeId}` });
    } catch (err: any) {
      result = err;
    }
    setBusy(false);

    if (result?.Revoked) {
      showNotification({ color: 'green.7', message: 'Invite revoked', autoClose: 3000 });
      onChanged();
      return;
    }

    showNotification({
      color: 'yellow.7',
      message: result?.Message || result?.message || 'Could not revoke the invite.',
      autoClose: 5000,
    });
  };

  const confirmRevoke = () =>
    setConfirmOptions({
      ...Helper.initialiseConfirmOptions(),
      display: true,
      heading: 'Revoke invite',
      text: revokeConfirmText,
      confirmButtonText: 'Revoke invite',
      onConfirm: revoke,
    });

  const waiting = status === 'pending';
  const expired = status === 'expired';

  const title = waiting ? 'Waiting for them to join' : expired ? 'Invite expired' : 'Invite revoked';
  const detail = waiting
    ? waitingLine(issuedAt, expiresAt)
    : expired
      ? expiredLine(expiresAt)
      : 'The link no longer works. Send a new invite if they still need access.';
  const icon = waiting ? <IconClock size={20} /> : expired ? <IconAlertCircle size={20} /> : <IconX size={20} />;

  return (
    <Paper withBorder p="md" mb="md" radius="md">
      <Flex gap="md" align="center">
        <ThemeIcon size={40} radius="xl" variant="light" color={waiting ? 'orange' : 'red'}>
          {icon}
        </ThemeIcon>
        <Box>
          <Text fw={700}>{title}</Text>
          {detail && <Text size="sm" c="dimmed">{detail}</Text>}
        </Box>
      </Flex>

      <Flex gap="sm" mt="md" align="center" wrap="wrap">
        {waiting ? (
          <>
            <Button variant="default" leftSection={<IconCopy size={14} />} loading={busy} onClick={copyLink}>
              Copy link
            </Button>
            <Button variant="default" leftSection={<IconBrandWhatsapp size={14} />} loading={busy} onClick={shareOnWhatsApp}>
              WhatsApp
            </Button>
          </>
        ) : (
          <Button color="scBlue" loading={busy} onClick={resendAndShow}>
            Send a new invite
          </Button>
        )}
        <Box style={{ flex: 1 }} />
        {status !== 'revoked' && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" aria-label="More invite actions" disabled={busy}>
                <IconDots size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {waiting && (
                <Menu.Item onClick={resendAndShow}>
                  <Text size="sm">Resend invite</Text>
                  <Text size="xs" c="dimmed">Sends a new link. The old one stops working.</Text>
                </Menu.Item>
              )}
              <Menu.Item color="red" onClick={confirmRevoke}>Revoke invite</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Flex>

      <Modal opened={!!sharing} onClose={() => setSharing(null)} title="Invite link" centered>
        <Group gap="xs">
          <Button
            color="scBlue"
            leftSection={<IconCopy size={14} />}
            disabled={!sharing?.link}
            onClick={() => copy(sharing?.link)}
          >
            Copy link
          </Button>
          <Button
            variant="outline"
            color="gray.7"
            leftSection={<IconBrandWhatsapp size={14} />}
            component="a"
            href={sharing?.whatsAppUrl}
            target="_blank"
            rel="noreferrer"
            disabled={!sharing?.whatsAppUrl}
          >
            WhatsApp
          </Button>
        </Group>
        <Text size="sm" c="dimmed" mt="md">Link expires in 7 days and can only be used once.</Text>
      </Modal>
    </Paper>
  );
}

export default EmployeeInvitePanel;
